import { useQuery } from '@tanstack/react-query';

const IS_TESTNET = (import.meta.env.VITE_TON_NETWORK || 'mainnet') === 'testnet';
const TONAPI_BASE = IS_TESTNET ? 'https://testnet.tonapi.io' : 'https://tonapi.io';

export interface NftMeta {
  address: string;
  name: string | null;
  image: string | null;
  imageLarge: string | null;
  collectionName: string | null;
  collectionAddress: string | null;
  attributes: Record<string, string>;
}

interface TonApiNftResponse {
  address: string;
  metadata?: {
    name?: string;
    image?: string;
    attributes?: Array<{ trait_type: string; value: string }>;
  };
  previews?: Array<{ resolution: string; url: string }>;
  dns?: string;
  collection?: {
    address: string;
    name?: string;
  };
}

function parseAttributes(
  attrs: Array<{ trait_type: string; value: string }> | undefined,
): Record<string, string> {
  if (!attrs || !Array.isArray(attrs)) return {};
  const result: Record<string, string> = {};
  for (const attr of attrs) {
    if (attr.trait_type && attr.value != null) {
      result[attr.trait_type.toLowerCase().replace(/\s+/g, '_')] = String(attr.value);
    }
  }
  return result;
}

async function fetchNftMeta(nftAddress: string): Promise<NftMeta> {
  const res = await fetch(
    `${TONAPI_BASE}/v2/nfts/${encodeURIComponent(nftAddress)}`,
    { headers: { Accept: 'application/json' } },
  );

  if (!res.ok) {
    return { address: nftAddress, name: null, image: null, imageLarge: null, collectionName: null, collectionAddress: null, attributes: {} };
  }

  const data = (await res.json()) as TonApiNftResponse;
  const thumb = data.previews?.find((p) => p.resolution === '100x100') || data.previews?.[0];
  const large = data.previews?.find((p) => p.resolution === '500x500')
    || data.previews?.find((p) => p.resolution === '1500x1500');

  return {
    address: nftAddress,
    name: data.metadata?.name || data.dns || null,
    image: thumb?.url || data.metadata?.image || null,
    imageLarge: large?.url || thumb?.url || data.metadata?.image || null,
    collectionName: data.collection?.name || null,
    collectionAddress: data.collection?.address || null,
    attributes: parseAttributes(data.metadata?.attributes),
  };
}

export function useNftMetadata(nftAddress: string | undefined) {
  return useQuery<NftMeta>({
    queryKey: ['nftMeta', nftAddress],
    queryFn: () => fetchNftMeta(nftAddress!),
    enabled: !!nftAddress,
    staleTime: Infinity,
    gcTime: 24 * 60 * 60 * 1000,
  });
}
